import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  X, Image as ImageIcon, Smile,
  AtSign, Hash, Plus, Send, AlertCircle, Search, Loader2
} from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import EmojiPicker from 'emoji-picker-react';
import imageCompression from 'browser-image-compression';
import { router, usePage } from '@inertiajs/react';
import { UserAvatar } from "@/components/UserAvatar";
import { useConfirm } from "@/hooks/useConfirm";

// Constants
const POINTS_PRESETS = [50, 100, 150, 200, 250, 300, 350, 400, 500];

const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY;

export function GiveModal({ isOpen, onClose, users = [], companyValues = [], onSuccess }) {
  const { auth } = usePage().props;
  const currentUser = auth?.user;
  const senderProfile = currentUser; // In Rails user model has profile fields flattened
  const { confirm, ConfirmDialog } = useConfirm();

  const [content, setContent] = useState("");
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isSending, setIsSending] = useState(false);

  // Picker States
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [showValuePicker, setShowValuePicker] = useState(false);
  const [showPointsPicker, setShowPointsPicker] = useState(false);

  const [cursorRect, setCursorRect] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Attachment State (GIF or Image)
  const [attachment, setAttachment] = useState(null); // { type: 'gif' | 'image', url: string }

  const [showGifPicker, setShowGifPicker] = useState(false);
  const [giphySearch, setGiphySearch] = useState("");
  const [gifs, setGifs] = useState([]);
  const [isLoadingGifs, setIsLoadingGifs] = useState(false);

  // Emoji Picker State
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [points, setPoints] = useState(0);
  // Calcular presupuesto disponible (allowance + boost - gastado)
  const REMAINING_BUDGET = (senderProfile?.monthly_allowance || 0) + (senderProfile?.allowance_boost || 0) - (senderProfile?.monthly_spent || 0);

  const closeAllPickers = () => {
    setShowUserPicker(false);
    setShowValuePicker(false);
    setShowPointsPicker(false);
  };

  const calculateTotalCost = (text) => {
    // 1. Find Base Points (single value)
    const pointsMatch = text.match(/\+(\d+)\s*pts/);
    const basePoints = pointsMatch ? parseInt(pointsMatch[1]) : 0;

    // 2. Count Users (count @ symbol)
    const userMatches = text.match(/@/g);
    const userCount = userMatches ? userMatches.length : 0;

    // 3. Calculate Total: Base * Users
    return basePoints * Math.max(userCount, 1);
  };

  const updateStateFromEditor = () => {
    if (!editorRef.current) return;
    const text = editorRef.current.innerText;
    setContent(text);
    setPoints(calculateTotalCost(text));
  };

  useEffect(() => {
    if (!isOpen) {
      setContent("");
      setPoints(0);
      setAttachment(null);
      setGifs([]);
      setGiphySearch("");
      setSearchQuery("");
      setShowGifPicker(false);
      setShowEmojiPicker(false);
      closeAllPickers();
      setIsSending(false);
      if (editorRef.current) editorRef.current.innerHTML = "";
    }
  }, [isOpen]);

  // Handle Escape key to close pickers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showGifPicker || showEmojiPicker || showUserPicker || showValuePicker || showPointsPicker) {
          // Prevent the dialog from closing
          e.preventDefault();
          e.stopPropagation();

          setShowGifPicker(false);
          setShowEmojiPicker(false);
          closeAllPickers();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showGifPicker, showEmojiPicker, showUserPicker, showValuePicker, showPointsPicker]);

  // Fetch Trending GIFs on load
  useEffect(() => {
    if (showGifPicker && !giphySearch && GIPHY_API_KEY) {
      fetchGifs('trending');
    }
  }, [showGifPicker]);

  const fetchGifs = async (type, query = '') => {
    if (!GIPHY_API_KEY) return;
    setIsLoadingGifs(true);
    try {
      const endpoint = type === 'search'
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${query}&limit=20&rating=g`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=g`;

      const res = await fetch(endpoint);
      const data = await res.json();
      setGifs(data.data || []);
    } catch (error) {
      console.error("Error fetching GIFs:", error);
    } finally {
      setIsLoadingGifs(false);
    }
  };

  const handleGiphySearch = (e) => {
    setGiphySearch(e.target.value);
    if (e.target.value.length > 2) {
      fetchGifs('search', e.target.value);
    } else if (e.target.value.length === 0) {
      fetchGifs('trending');
    }
  };

  const handleInput = (e) => {
    const text = e.target.innerText;
    setContent(text);
    setPoints(calculateTotalCost(text));

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const textNode = range.startContainer;

      if (textNode.nodeType === 3) {
        const textContent = textNode.textContent;
        const caretPos = range.startOffset;
        const words = textContent.slice(0, caretPos).split(/\s+/);
        const currentWord = words[words.length - 1];

        if (currentWord) {
          const rect = range.getBoundingClientRect();
          if (currentWord.startsWith("@")) {
            setCursorRect(rect);
            setShowUserPicker(true);
            setShowValuePicker(false);
            setShowPointsPicker(false);
            setSearchQuery(currentWord.substring(1));
          } else if (currentWord.startsWith("#")) {
            setCursorRect(rect);
            setShowValuePicker(true);
            setShowUserPicker(false);
            setShowPointsPicker(false);
            setSearchQuery(currentWord.substring(1));
          } else if (currentWord.startsWith("+")) {
            setCursorRect(rect);
            setShowPointsPicker(true);
            setShowUserPicker(false);
            setShowValuePicker(false);
            setSearchQuery(currentWord.substring(1));
          } else {
            closeAllPickers();
          }
        } else {
          closeAllPickers();
        }
      } else {
        closeAllPickers();
      }
    } else {
      closeAllPickers();
    }
  };

  const insertElement = (html) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);

    const textNode = range.startContainer;
    if (textNode.nodeType === 3) {
      const textContent = textNode.textContent;
      const caretPos = range.startOffset;
      const words = textContent.slice(0, caretPos).split(/\s+/);
      const currentWord = words[words.length - 1];
      if (currentWord && (currentWord.startsWith("@") || currentWord.startsWith("#") || currentWord.startsWith("+"))) {
        range.setStart(textNode, caretPos - currentWord.length);
        range.deleteContents();
      }
    }

    const span = document.createElement("span");
    span.innerHTML = html + "&nbsp;";
    span.contentEditable = "false";
    range.insertNode(span);
    range.setStartAfter(span);
    range.setEndAfter(span);
    selection.removeAllRanges();
    selection.addRange(range);

    closeAllPickers();
    editorRef.current.focus();
    updateStateFromEditor();
  };

  const handleToolbarClick = (trigger) => {
    editorRef.current.focus();
    document.execCommand("insertText", false, trigger);

    const rect = editorRef.current.getBoundingClientRect();
    setCursorRect({ left: rect.left + 20, bottom: rect.top + 40 });

    if (trigger === '@') setShowUserPicker(true);
    if (trigger === '#') setShowValuePicker(true);
    if (trigger === '+') setShowPointsPicker(true);
    setSearchQuery("");
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };

      const compressedFile = await imageCompression(file, options);

      const previewUrl = URL.createObjectURL(compressedFile);

      setAttachment({
        type: 'image',
        url: previewUrl,
        file: compressedFile
      });
    } catch (error) {
      console.error('Error compressing image:', error);
      await confirm({
        title: 'Error',
        message: 'Hubo un error al procesar la imagen.',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'danger'
      });
    }

    e.target.value = null;
  };

  const lastRange = useRef(null);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Ensure the selection is inside the editor
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        lastRange.current = range.cloneRange();
      }
    }
  };

  const handleEmojiClick = (emojiObject) => {
    // 1. Restore selection
    if (lastRange.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(lastRange.current);
    } else {
      editorRef.current.focus();
    }

    // 2. Insert
    document.execCommand("insertText", false, emojiObject.emoji);

    // 3. Update State
    updateStateFromEditor();

    // 4. Update 'lastRange' to be after the emoji
    saveSelection();

    setShowEmojiPicker(false);
  };

  const CHIP_BTN_BASE = "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold transition-all shadow-sm active:scale-95 cursor-pointer";
  const CHIP_ICON_BASE = "w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm text-xs transition-transform group-hover:scale-110";
  const PICKER_MENU_BASE = "bg-white border border-gray-100 shadow-xl rounded-2xl z-[60] max-h-72 overflow-y-auto overflow-x-hidden";
  const PICKER_ITEM_BASE = "flex items-center w-full p-3.5 transition-all text-left border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer";

  const isOverBudget = points > REMAINING_BUDGET;

  const handleSend = async () => {
    if (!currentUser) return;
    setIsSending(true);

    // 1. Parse Recipients
    const mentionedUserNames = users.filter(u => content.includes(`@${u.full_name}`));

    if (mentionedUserNames.length === 0) {
      await confirm({
        title: 'Falta mencionar usuario',
        message: 'Debes mencionar a alguien válido (@Nombre).',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'warning'
      });
      setIsSending(false);
      return;
    }

    // 2. Parse Hashtag (using companyValues prop)
    const extractedHashtag = companyValues.find(v => content.includes(v.hashtag))?.hashtag;
    if (!extractedHashtag) {
      await confirm({
        title: 'Falta valor',
        message: 'Debes incluir un valor (#Valor).',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'warning'
      });
      setIsSending(false);
      return;
    }

    // 3. Points
    const pointsMatch = content.match(/\+(\d+)\s*pts/);
    const pointsPerUser = pointsMatch ? parseInt(pointsMatch[1]) : 0;

    if (pointsPerUser <= 0) {
      await confirm({
        title: 'Faltan puntos',
        message: 'Debes asignar puntos (+50 pts).',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'warning'
      });
      setIsSending(false);
      return;
    }

    // Check balance (allowance + boost - spent)
    const totalCost = pointsPerUser * mentionedUserNames.length;
    const totalAvailable = (senderProfile?.monthly_allowance || 0) + (senderProfile?.allowance_boost || 0) - (senderProfile?.monthly_spent || 0);
    if (totalCost > totalAvailable) {
      await confirm({
        title: 'Saldo insuficiente',
        message: 'No tienes suficiente saldo.',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'danger'
      });
      setIsSending(false);
      return;
    }

    // 4. Submit via Inertia
    const formData = {
      receiver_id: mentionedUserNames[0].id,
      points: pointsPerUser,
      message: content,
      hashtag: extractedHashtag,
      items: mentionedUserNames.map(u => u.id), // Send all receiver IDs
    };

    // Add attachment (GIF or Image)
    if (attachment?.type === 'gif') {
      formData.gif_url = attachment.url;
    } else if (attachment?.type === 'image' && attachment?.file) {
      formData.image = attachment.file;
    }

    router.post('/recognitions', {
      recognition: formData
    }, {
      forceFormData: true,
      preserveState: false,
      preserveScroll: true,
      onSuccess: () => {
        setContent("");
        setPoints(0);
        setAttachment(null);
        if (editorRef.current) editorRef.current.innerHTML = "";
        setIsSending(false);
        onClose();
      },
      onError: async (errors) => {
        console.error("Error creating recognition:", errors);
        await confirm({
          title: 'Error al enviar',
          message: 'Hubo un error al enviar el reconocimiento.',
          confirmText: 'Entendido',
          cancelText: '',
          variant: 'danger'
        });
        setIsSending(false);
      }
    });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title=""
      className="max-w-2xl p-0 overflow-visible rounded-3xl"
    >
      {/* HEADER */}
      <div className="bg-gradient-to-br from-bambu-green to-bambu-dark-green p-6 h-32 rounded-t-3xl flex justify-between items-start text-white shadow-md relative overflow-hidden pr-16 font-montserrat">
        <div className="z-10 mt-2">
          <h2 className="text-3xl font-extrabold tracking-tight">Dar reconocimiento</h2>
          <p className="text-bambu-green-pastel opacity-90 mt-1 font-medium">¡Celebra los logros de tu equipo!</p>
        </div>
        <div className="flex flex-col items-end z-10 mt-1">
          <span className="text-[10px] uppercase tracking-widest opacity-80 font-bold mb-1">TU PRESUPUESTO</span>
          <div className={cn("px-4 py-1.5 rounded-full border shadow-lg backdrop-blur-md transition-colors", isOverBudget ? "bg-red-500/20 border-red-200 text-red-100" : "bg-white/20 border-white/30")}>
            <span className="font-extrabold text-xl">{REMAINING_BUDGET - points} pts</span>
          </div>
          <div className="text-[9px] opacity-75 mt-1 text-white/90">
            {senderProfile?.monthly_allowance || 0} {(senderProfile?.allowance_boost || 0) > 0 && `+ ${senderProfile.allowance_boost} boost`} {(senderProfile?.monthly_spent || 0) > 0 && `- ${senderProfile.monthly_spent} gastado`}
          </div>
        </div>
      </div>

      {/* EDITOR (Scrollable) */}
      <div className="p-8 pb-4 overflow-y-auto max-h-[50vh] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        <div className="flex gap-4 mb-6">
          <button onClick={() => handleToolbarClick('@')} className={cn(CHIP_BTN_BASE, "bg-blue-50 text-blue-600 hover:bg-blue-100 group")}>
            <div className={CHIP_ICON_BASE}><AtSign className="w-3.5 h-3.5 text-blue-500" /></div> Mencionar
          </button>
          <button onClick={() => handleToolbarClick('#')} className={cn(CHIP_BTN_BASE, "bg-green-50 text-green-600 hover:bg-green-100 group")}>
            <div className={CHIP_ICON_BASE}><Hash className="w-3.5 h-3.5 text-green-500" /></div> Valor
          </button>
          <button
            onClick={() => points === 0 && handleToolbarClick('+')}
            className={cn(CHIP_BTN_BASE, points > 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50" : "bg-amber-50 text-amber-600 hover:bg-amber-100 group")}
          >
            <div className={cn(CHIP_ICON_BASE, points > 0 && "bg-gray-200 text-gray-400")}><Plus className="w-3.5 h-3.5" /></div> Puntos
          </button>
        </div>

        <div
          className="w-full min-h-[120px] text-xl text-gray-500 font-medium font-montserrat outline-none cursor-text empty:before:content-['Escribe_tu_mensaje_aquí...'] empty:before:text-gray-300 empty:before:font-normal leading-loose tracking-wide"
          ref={editorRef}
          contentEditable
          onInput={(e) => { handleInput(e); saveSelection(); }}
          onKeyUp={() => { updateStateFromEditor(); saveSelection(); }}
          onClick={() => { updateStateFromEditor(); saveSelection(); }}
          onBlur={saveSelection}
        />

        {/* MISSING REQUIREMENTS INDICATOR */}
        {
          (!content.includes("@") || !content.includes("#") || points === 0) && (
            <div className="flex items-center gap-2 mt-6 mb-2 text-xs font-semibold text-gray-400 select-none">
              <AlertCircle className="w-4 h-4" />
              <span>Para enviar, te falta:</span>
              <div className="flex gap-1.5">
                {!content.includes("@") && <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100">@Mencionar</span>}
                {!content.includes("#") && <span className="bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-100">#Valor</span>}
                {points === 0 && <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full border border-amber-100">+Puntos</span>}
              </div>
            </div>
          )
        }

        {/* ATTACHMENT PREVIEW - Below Text */}
        {
          attachment && (
            <div className="mt-2 relative w-full group animate-in fade-in zoom-in duration-300">
              <img
                src={attachment.url}
                className="w-full h-auto max-h-[500px] object-contain rounded-xl shadow-lg border border-gray-100 bg-gray-50"
                alt="Attachment"
              />
              <button
                onClick={() => setAttachment(null)}
                className="absolute -top-2 -right-2 bg-white text-gray-400 hover:text-red-500 rounded-full p-1.5 shadow-md border pointer-events-auto cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )
        }

        {/* Error Alert inside Editor Area */}
        <AnimatePresence>
          {isOverBudget && (
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="flex items-center gap-2 mt-4 text-red-500 bg-red-50 p-3 rounded-xl border border-red-100"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-bold">No puedes exceder tu saldo disponible de {REMAINING_BUDGET} pts.</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div >

      {/* FOOTER */}
      < div className="p-6 bg-gray-50/50 border-t border-gray-100/50 rounded-b-3xl flex items-center justify-between backdrop-blur-sm relative z-30" >
        <div className="flex gap-3">
          {/* 1. GIF Button */}
          <button
            onClick={() => setShowGifPicker(!showGifPicker)}
            className={cn("p-2.5 rounded-full transition-all cursor-pointer relative", showGifPicker ? "bg-bambu-green text-white shadow-lg shadow-bambu-green/30" : "text-gray-400 hover:bg-white hover:shadow-sm")}
          >
            <span className="font-bold text-xs">GIF</span>
          </button>

          {/* 2. Image Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2.5 text-gray-400 hover:bg-white hover:shadow-sm rounded-full transition-all cursor-pointer"
          >
            <ImageIcon className="w-5 h-5" />
          </button>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />

          {/* 3. Emoji Button */}
          <div className="relative">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={cn("p-2.5 rounded-full transition-all cursor-pointer", showEmojiPicker ? "bg-amber-100 text-amber-500" : "text-gray-400 hover:bg-white hover:shadow-sm")}
            >
              <Smile className="w-5 h-5" />
            </button>
            {showEmojiPicker && (
              <div className="absolute bottom-12 left-0 z-50 animate-in fade-in zoom-in duration-200">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  width={300}
                  height={400}
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={handleSend}
          disabled={points === 0 || !content.includes("@") || !content.includes("#") || isOverBudget || isSending}
          className="rounded-full px-10 py-6 text-lg font-bold shadow-xl hover:shadow-2xl bg-gradient-to-r from-bambu-green to-bambu-dark-green transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          <div className="flex items-center">
            {isSending ? (
              <> <Loader2 className="w-5 h-5 mr-2 animate-spin" /> <span>Enviando...</span> </>
            ) : (
              <> <Send className="w-5 h-5 mr-2" /> <span>Enviar</span> </>
            )}
          </div>
        </Button>
      </div >

      {/* FLOATING PICKERS */}
      < AnimatePresence >
        {showUserPicker && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            style={{ position: 'fixed', left: cursorRect?.left ?? '50%', top: (cursorRect?.bottom ?? '50%') + 10 }}
            className={PICKER_MENU_BASE + " w-72"}
          >
            {users
              .filter(u => u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()))
              .filter(u => !content.includes(`@${u.full_name}`)) // Prevent duplicate users
              .map(u => (
                <button
                  key={u.id} className={PICKER_ITEM_BASE + " hover:bg-blue-50 group"}
                  onClick={() => insertElement(`<span class="inline-flex items-center align-middle px-3 py-1 rounded-full text-sm font-bold border mx-1 shadow-sm select-none text-blue-600 bg-blue-50 border-blue-100">@${u.full_name}</span>`)}
                >
                  <UserAvatar user={u} size={36} className="mr-3 border-2 border-white shadow-sm" />
                  <div>
                    <p className="font-bold text-sm text-gray-700 group-hover:text-blue-700">{u.full_name}</p>
                    <p className="text-xs text-gray-400 group-hover:text-blue-400">
                      {u.department?.name}
                    </p>
                  </div>
                </button>
              ))}
            {/* Show empty state if all filtered */}
            {users.filter(u => u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) && !content.includes(`@${u.full_name}`)).length === 0 && (
              <div className="p-4 text-center text-gray-400 text-sm italic">
                {searchQuery ? "No se encontraron usuarios" : "Ya mencionaste a todos"}
              </div>
            )}
          </motion.div>
        )
        }

        {
          showValuePicker && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              style={{ position: 'fixed', left: cursorRect?.left ?? '50%', top: (cursorRect?.bottom ?? '50%') + 10 }}
              className={PICKER_MENU_BASE + " w-72"}
            >
              {companyValues
                .filter(v => v.hashtag.toLowerCase().includes(searchQuery.toLowerCase()))
                .filter(v => !content.includes(`${v.hashtag}`)) // Prevent duplicate values
                .map(v => (
                  <button
                    key={v.id} className={PICKER_ITEM_BASE + " hover:bg-green-50 group"}
                    onClick={() => insertElement(`<span class="inline-flex items-center align-middle px-3 py-1 rounded-full text-sm font-bold border mx-1 shadow-sm select-none text-green-600 bg-green-50 border-green-100">${v.hashtag}</span>`)}
                  >
                    <span className="text-2xl mr-3 filter group-hover:brightness-110 transition-all">{v.emoji}</span>
                    <div>
                      <p className="font-bold text-sm text-gray-700 group-hover:text-green-700">{v.hashtag}</p>
                      <p className="text-xs text-gray-400 group-hover:text-green-500">{v.description}</p>
                    </div>
                  </button>
                ))}
              {companyValues.filter(v => v.hashtag.toLowerCase().includes(searchQuery.toLowerCase()) && !content.includes(`${v.hashtag}`)).length === 0 && (
                <div className="p-4 text-center text-gray-400 text-sm italic">
                  {searchQuery ? "No se encontraron valores" : "Ya agregaste este valor"}
                </div>
              )}
            </motion.div>
          )
        }

        {
          showPointsPicker && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              style={{ position: 'fixed', left: cursorRect?.left ?? '50%', top: (cursorRect?.bottom ?? '50%') + 10 }}
              className={PICKER_MENU_BASE + " w-48"}
            >
              {/* Logic: If points > 0 already, show warning instead of options */}
              {points > 0 ? (
                <div className="p-4 text-center">
                  <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <p className="text-xs font-bold text-gray-500">Solo puedes dar un monto de puntos por reconocimiento.</p>
                </div>
              ) : (
                <>
                  <div className="px-4 py-2 bg-gray-50/50 text-[10px] uppercase font-bold text-gray-400 border-b tracking-wider">Seleccionar Cantidad</div>

                  {searchQuery && !isNaN(searchQuery) && !POINTS_PRESETS.includes(parseInt(searchQuery)) && (
                    <button
                      className={PICKER_ITEM_BASE + " hover:bg-amber-50 group"}
                      onClick={() => insertElement(`<span class="inline-flex items-center align-middle px-3 py-1 rounded-full text-sm font-bold border mx-1 shadow-sm select-none text-amber-600 bg-amber-50 border-amber-100">+${searchQuery} pts</span>`)}
                    >
                      <span className="font-bold text-amber-600 border border-amber-200 bg-white rounded-md px-2 py-0.5 shadow-sm">+{searchQuery}</span>
                      <span className="ml-auto text-xs text-gray-400 font-medium group-hover:text-amber-500">Personalizado</span>
                    </button>
                  )}

                  {POINTS_PRESETS.map(amt => (
                    <button
                      key={amt} className={PICKER_ITEM_BASE + " hover:bg-amber-50 group"}
                      onClick={() => insertElement(`<span class="inline-flex items-center align-middle px-3 py-1 rounded-full text-sm font-bold border mx-1 shadow-sm select-none text-amber-600 bg-amber-50 border-amber-100">+${amt} pts</span>`)}
                    >
                      <span className="font-bold text-gray-600 group-hover:text-amber-700 text-base">+{amt} pts</span>
                    </button>
                  ))}
                </>
              )}
            </motion.div>
          )
        }

        {
          showGifPicker && (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-24 left-8 right-8 h-96 bg-white border border-gray-100 shadow-2xl rounded-2xl z-[70] p-4 flex flex-col"
            >
              <div className="relative mb-4">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar en GIPHY..."
                  className="w-full bg-gray-50 rounded-xl py-2 pl-9 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-bambu-green/20"
                  value={giphySearch}
                  onChange={handleGiphySearch}
                  autoFocus
                />
                <div className="absolute right-3 top-2.5">
                  <span className="text-[10px] font-bold text-gray-300">GIPHY</span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-2 pr-1">
                {isLoadingGifs ? (
                  <div className="col-span-2 flex items-center justify-center h-full text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
                  </div>
                ) : gifs.length > 0 ? (
                  gifs.map(gif => (
                    <img
                      key={gif.id}
                      src={gif.images.fixed_height_small.url}
                      alt={gif.title}
                      className="w-full h-auto rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        setAttachment({ type: 'gif', url: gif.images.fixed_height.url });
                        setShowGifPicker(false);
                      }}
                    />
                  ))
                ) : (
                  <div className="col-span-2 flex items-center justify-center h-full text-gray-400 text-sm">
                    {GIPHY_API_KEY ? "No se encontraron GIFs" : "Falta configuración de API Key"}
                  </div>
                )}
              </div>
            </motion.div>
          )
        }
      </AnimatePresence >

      <ConfirmDialog />
    </Dialog >
  );
}
